/**
 * What put a story where it is in a feeds v2 list, for the admin badge.
 *
 * A feed or search orders its stories by `rank_score` = grade × relevance ×
 * time decay (`wave_feed_contract.ranked_stories`). For You then mixes its
 * feeds (`for_you_mix.combine`): each story a feed has already placed
 * multiplies that feed's next story by `SAME_FEED_PENALTY`, so For You's order
 * is by `rank_score × SAME_FEED_PENALTY^k`.
 */
import type { RankedStory } from './feedsV2';

type RankKeys = 'rank_score' | 'rank_grade' | 'rank_relevance' | 'rank_age_hours';

/** Mirror of `for_you_mix.SAME_FEED_PENALTY`; test_for_you_mix.py pins it. */
export const SAME_FEED_PENALTY = 0.95;

export interface RankFactors {
  score: number;
  grade: number;
  relevance: number;
  /** The time-decay factor the score carries: score / (grade × relevance); null when grade × relevance is 0. */
  decay: number | null;
  ageHours: number;
}

/** A served row's rank inputs; null for a row no v2 build ranked (a permalink, /fulltextsearch). */
export function rankFactors(story: Partial<Pick<RankedStory, RankKeys>>): RankFactors | null {
  const { rank_score: score, rank_grade: grade, rank_relevance: relevance, rank_age_hours: ageHours } = story;
  if (score == null || grade == null || relevance == null || ageHours == null) return null;
  const base = grade * relevance;
  return { score, grade, relevance, decay: base > 0 ? score / base : null, ageHours };
}

/** Where For You's mix placed a story: `placed` earlier stories share its best feed. */
export interface MixPlacement {
  feed: string;
  placed: number;
  factor: number;
  /** The score For You ordered it by: `rank_score × factor`. */
  score: number;
}

/**
 * Each story's mix placement, from For You's served order (best first, hidden
 * stories included, as the server sends them). A story's feed is its best,
 * the first of `for_you_searches`, as `for_you_mix._balance` reads it.
 */
export function forYouMix(stories: readonly Pick<RankedStory, 'id' | 'rank_score' | 'for_you_searches'>[]): Map<string, MixPlacement> {
  const placedByFeed = new Map<string, number>();
  const mix = new Map<string, MixPlacement>();
  for (const story of stories) {
    const best = story.for_you_searches?.[0];
    if (!best) continue;
    const placed = placedByFeed.get(best.id) ?? 0;
    const factor = SAME_FEED_PENALTY ** placed;
    mix.set(story.id, { feed: best.name, placed, factor, score: story.rank_score * factor });
    placedByFeed.set(best.id, placed + 1);
  }
  return mix;
}
